// Mute/unmute toggle for each video testimonial card. The slideshow/drag behavior
// itself is handled entirely by the theme's native slideshow component — this file
// only needs to own the one thing that's specific to this section.
class VideoMuteToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.button.addEventListener('click', () => {
      // The native video snippet defers the real <video> tag inside an inert
      // <template> until the visitor clicks the poster/play button — it doesn't
      // exist in the live DOM before that, so this has to look it up fresh on every
      // click rather than caching a reference at connectedCallback time (which would
      // just find nothing and silently no-op forever).
      const video = this.closest('.video-testimonials__media-wrap')?.querySelector('video');
      if (!video) return;

      video.muted = !video.muted;
      if (!video.muted) {
        this.setAttribute('unmuted', '');
        this.button.setAttribute('aria-label', 'Mute video');
      } else {
        this.removeAttribute('unmuted');
        this.button.setAttribute('aria-label', 'Unmute video');
      }
    });
  }
}

if (!customElements.get('video-mute-toggle')) {
  customElements.define('video-mute-toggle', VideoMuteToggle);
}

/**
 * Plays the given slide's video via deferred-media's own playMedia()/
 * showDeferredMedia() (media.js) — not a raw video.play() — since those are what
 * keep its internal isPlaying flag in sync (relevant for the toggle button's state,
 * before it was hidden per client request; still relevant for the single-active-
 * playback enforcement in initClickToPlay below, which listens for the native 'play'
 * event those methods dispatch). Pauses every other video in the same slideshow first.
 * @param {Element} slideshow
 * @param {Element | undefined} activeSlide
 */
function playActiveSlideVideo(slideshow, activeSlide) {
  const deferredMedia = activeSlide?.querySelector('deferred-media');
  if (!deferredMedia) return;

  slideshow.querySelectorAll('deferred-media').forEach((otherMedia) => {
    if (otherMedia !== deferredMedia && typeof otherMedia.pauseMedia === 'function') {
      otherMedia.pauseMedia();
    }
  });

  if (deferredMedia.getAttribute('data-media-loaded') && typeof deferredMedia.playMedia === 'function') {
    // Already loaded (a slide being revisited) — resume via the native method.
    deferredMedia.playMedia();
  } else if (typeof deferredMedia.showDeferredMedia === 'function') {
    // First time this slide becomes active — loads the deferred <video> and plays
    // it, correctly setting isPlaying in the same call.
    deferredMedia.showDeferredMedia();
  }
}

/**
 * Marks exactly one slide as the visually "active" (big/sharp) one, and plays its
 * video (see playActiveSlideVideo above), pausing every other slide's video.
 *
 * The native slideshow component stamps aria-hidden="false" on *every* slide that's
 * currently on-screen — on the 1-up mobile layout that's a single slide, but on the
 * 3-up desktop layout it's all 3 simultaneously, so a CSS rule keyed off aria-hidden
 * would grow/brighten all 3 visible cards at once instead of just the true center one.
 * This reacts to those same aria-hidden updates (via MutationObserver, since the
 * component doesn't expose a dedicated "visible slides changed" event) and picks the
 * middle element of the component's own `visibleSlides` list as the one true active
 * slide — autoplay reuses that exact same result rather than a second, potentially-
 * disagreeing notion of "active" (an earlier version of this file triggered autoplay
 * off slideshow:select instead, which isn't guaranteed to target the true center slide
 * on the 3-up layout).
 * @param {Element} slideshow
 * @param {Element[]} slides
 */
function initActiveSlide(slideshow, slides) {
  if (!slides.length) return;

  function applyActiveSlide() {
    const visible = slideshow.visibleSlides;
    if (!visible || !visible.length) return;

    const centerSlide = visible[Math.floor((visible.length - 1) / 2)];
    slides.forEach((slide) => {
      slide.classList.toggle('video-testimonials__slide--active', slide === centerSlide);
    });

    playActiveSlideVideo(slideshow, centerSlide);
  }

  applyActiveSlide();

  const observer = new MutationObserver(applyActiveSlide);
  slides.forEach((slide) => {
    observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
  });
}

/**
 * Click-to-play/pause on the video area itself, with only one video playing at a time.
 *
 * The native poster button (click to load + play) and the small toggle-hint button
 * (click to pause/resume once loaded) already work correctly on their own — the gap
 * is that clicking the video itself (outside those two small buttons) did nothing
 * once loaded. This adds that, routed through deferred-media's own showDeferredMedia()/
 * toggleMedia() so its isPlaying state stays correct.
 *
 * Single-active-playback is enforced via a capture-phase 'play' listener rather than
 * hooking every play-triggering path individually: HTMLMediaElement's play event
 * doesn't bubble, but a capture-phase listener on an ancestor still sees it on the way
 * down to the <video>, regardless of what triggered play() — poster click, toggle
 * click, autoplay (playActiveSlideVideo), or this handler — so it can't be bypassed by
 * any one entry point.
 * @param {Element} slideshow
 */
function initClickToPlay(slideshow) {
  slideshow.addEventListener(
    'play',
    (event) => {
      if (!(event.target instanceof HTMLVideoElement)) return;
      slideshow.querySelectorAll('video').forEach((video) => {
        if (video !== event.target) video.pause();
      });
    },
    true
  );

  slideshow.querySelectorAll('.video-testimonials__media-wrap').forEach((mediaWrap) => {
    const deferredMedia = mediaWrap.querySelector('deferred-media');
    if (!deferredMedia) return;

    mediaWrap.addEventListener('click', (event) => {
      // Native buttons inside media-wrap (poster button, toggle-hint button) already
      // have their own on:click handlers — let those run as-is, this is only for
      // clicks on the plain video area outside any button.
      if (event.target instanceof Element && event.target.closest('button')) return;

      if (deferredMedia.getAttribute('data-media-loaded') && typeof deferredMedia.toggleMedia === 'function') {
        deferredMedia.toggleMedia();
      } else if (typeof deferredMedia.showDeferredMedia === 'function') {
        deferredMedia.showDeferredMedia();
      }
    });
  });
}

/**
 * Steps the arrows one slide at a time and loops at the ends, instead of the native
 * defaults.
 *
 * The native previous()/next() advance by `visibleSlides.length` on a multi-up layout
 * (3 on desktop here) — built for "page through a whole new set of 3" carousels, not
 * this one, where each click should reveal just the next adjacent card. The component
 * exposes no option to change that step size.
 *
 * This can't be fixed by intercepting the click and calling stopPropagation(): the
 * native on:click="/next" delegation itself listens on `document` in the CAPTURE
 * phase (see component.js), and capture propagates root-to-target — so document's
 * listener always runs before any listener added directly on the button, no matter
 * how it's registered. Removing the on:click attribute is the only way to stop the
 * native handler from ever matching these buttons; a plain listener drives select()
 * directly with current ± 1 instead, wrapping via modulo at the ends. Looping this way
 * only works correctly because the section is rendered with infinite: true
 * (sections/video-testimonials.liquid) — without it, the native component disables
 * the previous arrow at slide 0 and the next arrow at the last slide regardless of
 * this custom step logic, and a disabled button doesn't fire click events at all.
 * @param {Element} slideshow
 */
function initSingleStepArrows(slideshow) {
  const previousButton = slideshow.querySelector('slideshow-arrows .slideshow-control--previous');
  const nextButton = slideshow.querySelector('slideshow-arrows .slideshow-control--next');

  // Our own authoritative index, deliberately never read back from the native
  // `slideshow.current` property. That property is computed by #sync() in slideshow.js
  // as "whichever visible slide sits closest to the scroller's *left edge*" on a
  // multi-visible (3-up desktop) layout — but each slide snaps to the scroller's
  // *center* (scroll-snap-align: center, slideshow-styles.liquid) when selected. So
  // selecting index N centers N, which makes N-1 read back as "current" — a
  // consistent drift between what we asked for and what we'd read back next. It's
  // invisible in the middle of the strip but skips a card at each end once the drift
  // crosses the wrap boundary. Tracking our own index sidesteps the mismatch entirely.
  //
  // NOTE: there's a second, separate issue still open — without reserved scroll space
  // past the first/last card (a "gutters" fix was tried and reverted: it visually broke
  // the layout by adding a real empty gap at both ends), the browser physically can't
  // scroll far enough to true-center the first or last card, so those two positions
  // still clamp to the nearest reachable spot rather than fully centering. This local
  // index at least keeps our own math correct and stops the "skips two cards" jump,
  // but the true first/last card still won't visually center — flagged for a follow-up
  // fix rather than guessed at again here.
  let currentIndex = 0;

  function step(direction) {
    const slides = slideshow.slides;
    if (!slides || !slides.length) return;

    const targetIndex = (currentIndex + direction + slides.length) % slides.length;

    // The native component's seamless "reorder the DOM for a short hop" infinite-loop
    // trick (slideshow.js select()) only runs when visibleSlides.length === 1 — true on
    // the 1-up mobile layout, never on this section's 3-up desktop layout. Without it,
    // wrapping from the last slide back to the first (or vice versa) falls through to a
    // real, animated scroll across the *entire* strip, which reads as the loop being
    // broken rather than infinite. Rather than extending that shared trick to every
    // multi-visible carousel site-wide, just make this section's own wrap step instant
    // (no long animated scroll-back) so it reads as a clean loop instead of a jarring
    // reverse sweep.
    const isWrap = (direction > 0 && targetIndex < currentIndex) || (direction < 0 && targetIndex > currentIndex);
    slideshow.select(targetIndex, undefined, { animate: !isWrap });
    currentIndex = targetIndex;
  }

  if (previousButton) {
    previousButton.removeAttribute('on:click');
    previousButton.addEventListener('click', () => step(-1));
  }

  if (nextButton) {
    nextButton.removeAttribute('on:click');
    nextButton.addEventListener('click', () => step(1));
  }
}

/**
 * Client request: on mobile, the first reel should be centered with a peek of the
 * next one on both sides, not flush against the left edge. CSS reserves the space
 * (slideshow_gutters: 'start end' + --gutter-slide-width, section-video-testimonials.css)
 * but reserving space doesn't move the scroll position into it — the browser still
 * starts at scrollLeft 0, which now just shows blank gutter first. This does that one
 * remaining scroll, computed directly from live layout (not a fixed pixel value, so
 * it stays correct regardless of the exact gutter/gap/slide-width chosen).
 *
 * Deliberately NOT done via the native slideshow.select() API: that call silently
 * no-ops before the component's [in-viewport] attribute appears (its internal
 * #scroll helper isn't set up yet) — confirmed the hard way on an earlier, reverted
 * attempt at this exact fix. Setting scrollLeft directly on the track bypasses that
 * entirely, so it only needs [in-viewport] to be true for the element to actually be
 * scrollable, not any of the component's own internal readiness state.
 * @param {Element} slideshow
 */
function initMobileInitialCenter(slideshow) {
  if (!window.matchMedia('(max-width: 749px)').matches) return;

  const track = slideshow.querySelector('slideshow-slides');
  const firstSlide = track?.querySelector('.video-testimonials__slide');
  if (!track || !firstSlide) return;

  function centerFirstSlide() {
    const trackRect = track.getBoundingClientRect();
    const slideRect = firstSlide.getBoundingClientRect();
    const slideCenter = slideRect.left + slideRect.width / 2 - trackRect.left + track.scrollLeft;
    track.scrollLeft = Math.max(0, slideCenter - trackRect.width / 2);
  }

  if (slideshow.hasAttribute('in-viewport')) {
    centerFirstSlide();
    return;
  }

  const observer = new MutationObserver(() => {
    if (!slideshow.hasAttribute('in-viewport')) return;
    centerFirstSlide();
    observer.disconnect();
  });
  observer.observe(slideshow, { attributes: true, attributeFilter: ['in-viewport'] });
}

/**
 * Pauses whatever's playing once the section scrolls off-screen. Without this,
 * scrolling past a playing testimonial (with sound) leaves it running invisibly in
 * the background — wasted bandwidth/battery, and audio that keeps going after the
 * visitor's moved on.
 * @param {Element} slideshow
 */
function initPauseOffscreen(slideshow) {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) return;
        slideshow.querySelectorAll('deferred-media').forEach((media) => {
          if (typeof media.pauseMedia === 'function') media.pauseMedia();
        });
      });
    },
    { threshold: 0 }
  );
  observer.observe(slideshow);
}

/**
 * Client request: no visible play/pause button at all. Already force-hidden via CSS
 * (.video-testimonials .video-interaction-hint { display: none !important; }) — this
 * removes it from the DOM outright too, on top of that, so there's nothing left to
 * find or accidentally re-show. deferred-media only creates the button if it exists in
 * the initial server-rendered markup (it doesn't add one dynamically), so a one-time
 * sweep is enough — no observer needed for elements added later.
 * @param {Element} slideshow
 */
function removePlayPauseButton(slideshow) {
  slideshow.querySelectorAll('.video-interaction-hint').forEach((button) => button.remove());
}

/**
 * Wires up everything for one video-testimonials slideshow instance. Kept as a single
 * per-instance entry point (rather than five separate top-level functions each doing
 * their own document.querySelectorAll('.video-testimonials__slideshow').forEach(...))
 * so there's exactly one place that needs updating if a step here is renamed, and one
 * ready-state check below instead of two branches that can silently drift out of sync.
 * @param {Element} slideshow
 */
function initVideoTestimonialsSlideshow(slideshow) {
  const slides = Array.from(slideshow.querySelectorAll('.video-testimonials__slide'));

  removePlayPauseButton(slideshow);
  initClickToPlay(slideshow);
  initActiveSlide(slideshow, slides);
  initSingleStepArrows(slideshow);
  initMobileInitialCenter(slideshow);
  initPauseOffscreen(slideshow);
}

function initAllVideoTestimonialsSlideshows() {
  document.querySelectorAll('.video-testimonials__slideshow').forEach(initVideoTestimonialsSlideshow);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllVideoTestimonialsSlideshows);
} else {
  initAllVideoTestimonialsSlideshows();
}
